import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Countries } from '@models/countries.model';
import { CountryItem } from '@models/dashboard.model';
import { ShoppingCartData, TravelDetailsItem } from '@models/shopping-cart.model';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { CountriesService } from '@services/countries/countries.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { DatePickerComponent } from '@shared-components/custom-fields/date-picker/date-picker.component';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { formatNgbDate } from '@utils/custom-form.utils';
import { Subject, Subscription } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

@Component({
  selector: 'ts-activity-6-travel-details',
  templateUrl: './travel-details.component.html',
  imports: [
    ReactiveFormsModule,
    TranslationPipe,
    DataLoaderDirective,
    DatePickerComponent,
    PriceSummaryComponent
  ]
})
export class TravelDetailsComponent implements OnInit, AfterViewChecked, OnDestroy {
  form: FormGroup;
  form_array: FormArray;
  countryList: CountryItem[];
  shopping_cart: ShoppingCartData;
  refresh_summary: Subject<string | boolean> = new Subject<string | boolean>();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_svc: ActivityManagerService,
    private cd: ChangeDetectorRef,
    private country_svc: CountriesService,
    private fb: FormBuilder,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.getCountryList();
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get limitTravelDetails(): boolean {
    return this.form_array && this.form_array.controls.length < 5;
  }

  get countryLabel(): string {
    return this.translation_svc.translate('country');
  }

  ngbDate = (value: NgbDateStruct): string | null => formatNgbDate(value, '/');

  formatDate(value: string): NgbDateStruct {
    let parts: string[] = value?.split('/');

    return {
      month: Number(parts[0]),
      day: Number(parts[1]),
      year: Number(parts[2])
    };
  }

  compareCountry = (x: CountryItem, y: CountryItem): boolean => x?.name === y?.name;

  onContinueSuccess(event: boolean): void {
    if (event && this.form.valid) {
      this.subscriptions.add(
        this.shopping_svc
          .sendShoppingItems(
            { ...this.shopping_cart, travel_details: this.form_array.value },
            false
          )
          .subscribe({
            next: (res: ShoppingCartData) => {
              this.activity_svc.navigateToNextStep();
            },
            error: (err: HttpErrorResponse) => {}
          })
      );
    }
  }

  getShoppingCartData(cart_data: ShoppingCartData): void {
    if (cart_data) {
      this.shopping_cart = cart_data;
      this.setupForm();
    }
  }

  addNewDetails(): void {
    if (this.limitTravelDetails) this.form_array?.push(this.travelDetailsGroup());
  }

  removeDetails(i: number): void {
    if (this.form_array && this.form_array.controls.length > 1) this.form_array.removeAt(i);
  }

  private setupForm(): void {
    const travel_detail: TravelDetailsItem[] | undefined = this.shopping_cart?.travel_details;
    this.form_array = travel_detail?.length
      ? this.fb.array(
          travel_detail.map((item: TravelDetailsItem) => {
            let group: FormGroup = this.travelDetailsGroup(item);
            Object.keys(group.controls).forEach((key: string) => group.get(key)?.markAsDirty());

            return group;
          })
        )
      : this.fb.array([this.travelDetailsGroup()]);
    this.form = this.fb.group({
      travel_details: this.form_array
    });
  }

  private travelDetailsGroup(travel_detail?: TravelDetailsItem): FormGroup {
    return new FormGroup({
      country: new FormControl(travel_detail?.country ?? '', [Validators.required]),
      arrival_date: new FormControl(travel_detail?.arrival_date ?? null, [Validators.required]),
      departure_date: new FormControl(travel_detail?.departure_date ?? null, [Validators.required])
    });
  }

  private getCountryList(): void {
    this.country_svc
      .getCountries('travel-details', {
        all: true,
        orderTypeId: this.activity_svc.state().parameter('type')?.order_type_id!
      })
      .subscribe({
        next: (res: Countries) => {
          this.countryList = res.countries;
          this.activity_svc.state().setSteps(res.progress_bar_config?.steps);
        },
        error: (err: HttpErrorResponse) => {}
      });
  }
}
